import React, { useState } from 'react';
import { fetchWithAuth, getUserInfo, saveUserInfo, saveThemes, getBranchTheme } from '../utils/auth';
import { applyTheme, resolveTheme, THEMES, THEME_LABELS } from '../theme';
import { API_URL } from '../config';

const AdminProfileModal = ({ onClose }) => {
  const user = getUserInfo();
  const [displayName, setDisplayName] = useState(user.display_name || user.email || '');
  const [theme, setTheme]             = useState(user.theme || '');
  const [password, setPassword]       = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [saved, setSaved]             = useState(false);

  const handleSave = async () => {
    setError('');
    setSaved(false);

    if (password && password !== confirmPw) {
      setError('Passwords do not match');
      return;
    }
    if (password && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const payload = { display_name: displayName, theme };
    if (password) payload.password = password;

    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/profile`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Update cached user info with new display name and theme
        saveUserInfo({ ...user, display_name: displayName, email: displayName, theme });
        // Apply new theme immediately
        saveThemes(getBranchTheme(), theme);
        applyTheme(resolveTheme(getBranchTheme(), theme, null));
        setPassword('');
        setConfirmPw('');
        setSaved(true);
      } else {
        setError(data.error || 'Update failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>My Profile</h3>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>
          {/* Read-only info */}
          <div style={styles.infoRow}>
            <span style={styles.label}>Username</span>
            <span style={styles.value}>{user.username}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Role</span>
            <span className={`role-badge role-${user.role}`}>{(user.role || '').replace('_', ' ')}</span>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />

          {/* Editable fields */}
          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input
              type="text"
              value={displayName}
              onChange={e => { setDisplayName(e.target.value); setSaved(false); }}
              placeholder="Your full name"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Color Theme</label>
            <select
              value={theme}
              onChange={e => { setTheme(e.target.value); setSaved(false); }}
              style={{ ...styles.input, cursor: 'pointer' }}
            >
              <option value="">Use Branch Default</option>
              {THEMES.map(t => (
                <option key={t} value={t}>{THEME_LABELS[t]}</option>
              ))}
            </select>
            <small style={{ fontSize: '11px', color: '#9ca3af' }}>Applies to games you host</small>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setSaved(false); }}
              placeholder="Leave blank to keep current"
              style={styles.input}
              autoComplete="new-password"
            />
          </div>

          {password && (
            <div style={styles.field}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={e => { setConfirmPw(e.target.value); setSaved(false); }}
                placeholder="Re-enter new password"
                style={styles.input}
              />
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
          {saved && <div style={styles.success}>Profile updated successfully</div>}
        </div>

        <div style={styles.footer}>
          <button className="btn-secondary btn-sm" onClick={onClose}>Close</button>
          <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
    paddingTop: '56px', paddingRight: '16px',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    width: '300px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
  },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '16px', color: '#6b7280', lineHeight: 1,
  },
  body: { padding: '16px' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    background: '#f8fafc',
  },
  infoRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '8px', fontSize: '14px',
  },
  field: { marginBottom: '12px' },
  label: {
    display: 'block', fontSize: '12px', fontWeight: '600',
    color: '#6b7280', marginBottom: '4px', minWidth: '70px',
  },
  value: { fontSize: '14px', color: '#111' },
  input: {
    width: '100%', padding: '7px 10px',
    border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', boxSizing: 'border-box',
  },
  error:   { color: '#dc2626', fontSize: '13px', marginTop: '8px' },
  success: { color: '#16a34a', fontSize: '13px', marginTop: '8px' },
};

export default AdminProfileModal;
